import React from "react";
import { inject, observer } from "mobx-react";
import { Link } from "react-router-dom";
import Select from "react-select";
import CellContent from "./CellContent";
import { StoreContext } from "../../../contexts/StoreContext";
import CombinationDetailsModal from "../../../modal-views/CombinationDetailsModal";

import "./PivotTable.scss";

@inject("detailViewStore", "previewStore", "popupStore", "cubeQueryWizardStore")
@observer
class PivotTable extends React.Component {
  state = {
    showCombinationDetailsModal: false,
    details: { type: null, tid: null },
  };

  handleToggleCombinationDetailsModal = (type, tid) => {
    this.setState((oldState) => ({
      ...oldState,
      showCombinationDetailsModal: !oldState.showCombinationDetailsModal,
    }));
    this.setState({ details: { type: type, tid: tid } });
  };

  // shouldn't be PureComponent if also @observer
  resize;

  tableRef = React.createRef();
  tableHeadRef = React.createRef();
  tableBodyRef = React.createRef();

  componentDidMount() {
    const { getEntityData } = this.context;
    const entityData = getEntityData.item;
    const tableData = this.processEntityData(entityData);

    if (!tableData || !tableData.axes) return;

    const yAxes = tableData.axes.filter((axis) => axis.orientation === "Y");
    const zAxis = tableData.axes.find((axis) => axis.orientation === "Z");
    const refYAxis = yAxes.find((axis) => {
      return (
        Math.max(...yAxes.map((a) => axis.axisOrdinates.length)) ===
        axis.axisOrdinates.length
      );
    });

    const members = this.getMembers();
    const defaultMember = members.length > 0 ? members[0] : "n/a";

    if (zAxis?.axisOrdinates?.length === 1)
      zAxis.axisOrdinates[0].ordinateItem?.forEach((item, index) => {
        let options = this.getDefaultOptionsForZ(item);
        if (options && options[0])
          this.handleChange([options[0]], zAxis, index);
      });
    else if (zAxis?.axisOrdinates?.length > 1) {
      this.handleChange([defaultMember], zAxis, 0);
    }
    this.props.cubeQueryWizardStore.setHasMembersInZAxis(
      [defaultMember],
      zAxis
    );
    this.props.cubeQueryWizardStore.setHasOpenYAxis(yAxes, refYAxis);
    this.updateCubeCache();

    window.addEventListener("resize", this.handleResize);
    this.makeHeadSticky();
  }

  updateCubeCache = () => {
    const { getEntityData } = this.context;
    const entityData = getEntityData.item;
    const { getSelectedTemplateMembers } = this.context;
    const tableData = this.processEntityData(entityData);
    let combinations = this.getCombinations();
    const xAxes = tableData.axes
      .filter((axis) => axis.orientation === "X")
      .sort((a, b) => {
        if (a.orientation > b.orientation) return -1;
        if (a.orientation < b.orientation) return 1;
        return 0;
      });
    const yAxes = tableData.axes.filter((axis) => axis.orientation === "Y");
    const zAxis = tableData.axes.find((axis) => axis.orientation === "Z");
    if (!xAxes || !yAxes) return null;
    const refXAxis = xAxes.find((axis) => {
      return (
        Math.max(...xAxes.map(() => axis.axisOrdinates.length)) ===
        axis.axisOrdinates.length
      );
    });
    const refYAxis = yAxes.find((axis) => {
      return (
        Math.max(...yAxes.map((a) => axis.axisOrdinates.length)) ===
        axis.axisOrdinates.length
      );
    });
    const members = this.getMembers();
    const defaultMember =
      members.length > 0 && zAxis?.isOpenAxis !== true ? members[0] : "n/a";
    if (zAxis?.axisOrdinates?.length > 1) {
      combinations = defaultMember.combinationIds;
    }
    let selectedMember = getSelectedTemplateMembers;
    this.props.cubeQueryWizardStore.setHasMembersInZAxis(defaultMember, zAxis);
    this.props.cubeQueryWizardStore.setHasOpenYAxis(yAxes, refYAxis);
    this.props.cubeQueryWizardStore.createCubeXYZ(
      refXAxis,
      refYAxis,
      zAxis,
      combinations,
      this.props.itemIndex,
      tableData.tableCode,
      selectedMember
    );
  };

  componentDidUpdate() {
    const { setTableWidth } = this.props;
    this.makeHeadSticky();
    setTableWidth(this.getTableWidth());
    this.updateCubeCache();
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
  }

  handleResize = () => {
    clearTimeout(this.resize);
    this.resize = setTimeout(() => {
      this.makeHeadSticky();
    }, 300);
  };

  findMembers = (data, acc) => {
    if (data?.object?.type === "Member") {
      acc.push(data);
    } else {
      if (data.items)
        for (let i = 0; i < data.items.length; i += 1) {
          const currentChild = data.items[i];
          this.findMembers(currentChild, acc);
        }
    }
  };

  getDefaultOptionsForZ = (ordinateItem) => {
    let acc = [];
    this.findMembers(ordinateItem, acc);
    let OrdinateMembers = acc;
    if (OrdinateMembers.length > 0) {
      return OrdinateMembers.map((member) => {
        return member.object;
      });
    }
  };

  handleChange = (items, zAxis, index) => {
    const { setSelectedTemplateMembers } = this.context;
    if (
      zAxis &&
      zAxis.axisOrdinates &&
      items &&
      items.length > 0 &&
      items[0].type === "AxisOrdinate"
    ) {
      // the condition above should be equivalent to
      //   `this.props.cubeQueryWizardStore.hasMembersInZAxis === false`
      const targetAxisOrdinate = zAxis.axisOrdinates.find(
        (ao) => ao.tid === items[0].tid
      );
      items[0].combinationIds = targetAxisOrdinate?.combinationIds;
    }
    setSelectedTemplateMembers(items, this.props.itemIndex, index);
    this.makeHeadSticky();
    this.updateCubeCache();
  };

  processEntityData = (entityData) => {
    if (
      !entityData ||
      !entityData.navigationLookupResult ||
      !entityData.navigationLookupResult.navigationLookupRootItem ||
      !entityData.navigationLookupResult.navigationLookupRootItem.items ||
      !entityData.navigationLookupResult.navigationLookupRootItem.items.length
    )
      return;
    const cubeToTableItem = this.props.cubeToTableItem
      ? this.props.cubeToTableItem
      : entityData.navigationLookupResult.navigationLookupRootItem.items.filter(
          (item) => item.object.type === "CubeToTable"
        );
    if (!cubeToTableItem || !cubeToTableItem.items) return;
    const table = cubeToTableItem.items.find(
      (item) => item.object.type === "Table"
    );

    if (!table || !table.items || !table.items.length) return;

    const getName = (axisOrdinate) => {
      const ordinateItem =
        axisOrdinate.items && axisOrdinate.items.length
          ? axisOrdinate.items.find(
              (item) => item.object.type === "OrdinateItem"
            )
          : null;
      const variableItem =
        ordinateItem.items && ordinateItem.items.length
          ? ordinateItem.items.find((item) => item.object.type === "Variable")
          : null;
      return axisOrdinate && variableItem
        ? axisOrdinate.object.name
          ? axisOrdinate.object.name
          : variableItem.object.name
        : ""; // show name, if not present then code in brackets
    };
    const getVariable = (axisOrdinate) => {
      const ordinateItem =
        axisOrdinate.items && axisOrdinate.items.length
          ? axisOrdinate.items.filter(
              (item) => item.object.type === "OrdinateItem"
            )
          : null;
      const variableItem =
        ordinateItem && ordinateItem.items && ordinateItem.items.length
          ? ordinateItem.items.find((item) => item.object.type === "Variable")
          : undefined;
      return variableItem;
    };

    const tableData = {
      tableCode: table.object.code,
      axes: table.items
        .filter((i) => i.object && i.object.type === "Axis")
        .sort((a, b) => {
          if (a.object.orderNo > b.object.orderNo) return 1;
          if (a.object.orderNo < b.object.orderNo) return -1;
          return 0;
        })
        .map((axis) => {
          return {
            tid: axis.object.tid,
            type: axis.object.type,
            axisId: axis.object.axisId,
            orientation: axis.object.orientation,
            orderNo: axis.object.orderNo,
            name: axis.object.name,
            isOpenAxis: axis.object.isOpenAxis,
            maxLevelNo: axis.items
              ? Math.max(
                  ...axis.items
                    .filter(
                      (item) =>
                        item.object && item.object.type === "AxisOrdinate"
                    )
                    .map((item) => item.object.levelNo)
                )
              : 0,
            axisOrdinates: axis.items
              ? axis.items
                  .filter(
                    (item) => item.object && item.object.type === "AxisOrdinate"
                  )
                  .sort((a, b) => {
                    if (a.object.orderNo > b.object.orderNo) return 1;
                    if (a.object.orderNo < b.object.orderNo) return -1;
                    return 0;
                  })
                  .map((ordinate) => {
                    const obj = {
                      ordinateItem:
                        ordinate.items && ordinate.items.length
                          ? ordinate.items.filter(
                              (item) => item.object.type === "OrdinateItem"
                            )
                          : [],
                      tid: ordinate.object.tid,
                      type: ordinate.object.type,
                      axisId: ordinate.object.axisId,
                      axisOrdinateId: ordinate.object.axisOrdinateId,
                      orderNo: ordinate.object.orderNo,
                      levelNo: ordinate.object.levelNo,
                      code: ordinate.object.code,
                      name: ordinate.object.name
                        ? ordinate.object.name
                        : getName(ordinate),
                      variable: getVariable(ordinate),
                      isAbstractHeader: ordinate.object.isAbstractHeader,
                      combinationIds:
                        ordinate.items && ordinate.items.length
                          ? ordinate.items
                              .map((item) =>
                                item.items && item.items.length
                                  ? item.items
                                      .map((item) => item.object.combinationId)
                                      .filter((combId) => !!combId)
                                  : []
                              )
                              .reduce((acc, arr) => acc.concat(arr), [])
                          : [],
                      systemDataCodes:
                        ordinate.items && ordinate.items.length
                          ? ordinate.items
                              .map((item) =>
                                item.items && item.items.length
                                  ? item.items
                                      .map((item) => item)
                                      .filter(
                                        (combId) =>
                                          !!combId.object.combinationId
                                      )
                                      .map(
                                        (tableCell) =>
                                          tableCell.object.systemDataCode
                                      )
                                      .filter(
                                        (systemDataCode) => !!systemDataCode
                                      )
                                  : []
                              )
                              .reduce((acc, arr) => acc.concat(arr), [])
                          : [],
                    };
                    return obj;
                  })
                  .sort((a, b) => {
                    if (a.code > b.code) return 1;
                    if (a.code < b.code) return -1;
                    return 0;
                  })
              : [],
          };
        }),
    };
    return tableData;
  };

  getCellState = (combinationId, x, y, itemIndex) => {
    return this.props.cubeQueryWizardStore.getTemplateRepresentationCellState(
      combinationId,
      x,
      y,
      itemIndex
    );
  };

  renderTableHead = (
    xAxes,
    yAxes,
    zAxis,
    refXAxis,
    refYAxis,
    members,
    itemIndex
  ) => {
    const { getSelectedTemplateMembers } = this.context;
    let availableMember = [];
    const getChildrenNo = (ordinate, axisOrdinates) => {
      const nextAtSameOrHigherLevel = axisOrdinates.find(
        (o) => (o.levelNo <= ordinate.levelNo) & (o.code > ordinate.code)
      );
      if (!nextAtSameOrHigherLevel) {
        return axisOrdinates.filter((o) => o.code > ordinate.code).length;
      } else {
        return axisOrdinates.filter(
          (o) => o.code > ordinate.code && o.code < nextAtSameOrHigherLevel.code
        ).length;
      }
    };
    const generateFillers = (len, refLen, mod = 0) => {
      if (len < refLen) {
        return [...Array(refLen - len + mod)].map((f, index) => (
          <th key={index} style={{ border: "none" }}></th>
        ));
      } else {
        return null;
      }
    };

    const getOptionsForZ = (axisOrdinates, ordinateItem) => {
      let acc = [];
      if (axisOrdinates.length && axisOrdinates.length > 1) {
        let uniqueAxisOrdinates = this.getUniqueMembersCode(axisOrdinates);
        uniqueAxisOrdinates.forEach((member) => {
          const name = member.name;
          if (!availableMember.some((item) => item.name === name)) {
            availableMember.push(member);
          }
        });
        return uniqueAxisOrdinates.map((member) => {
          return {
            label: `${member?.name} (${member?.code})`,
            value: member?.tid,
          };
        });
      } else {
        this.findMembers(ordinateItem, acc);
        let OrdinateMembers = acc;
        for (let objects of acc) {
          availableMember.push(objects.object);
        }
        if (OrdinateMembers.length > 0) {
          return OrdinateMembers.map((member) => {
            return {
              label: `${member?.object?.name} (${member?.object?.code})`,
              value: member?.object?.tid,
            };
          });
        }
      }
    };
    const getValuesForZ = (index) => {
      let selectedMember = getSelectedTemplateMembers;
      if (
        selectedMember &&
        this.props.itemIndex < selectedMember.length &&
        selectedMember[this.props.itemIndex] &&
        index < selectedMember[this.props.itemIndex].length
      ) {
        if (selectedMember[this.props.itemIndex][index].length > 1) {
          return selectedMember[this.props.itemIndex][index].map(
            (member) => member.tid
          );
        } else {
          return [selectedMember[this.props.itemIndex][index][0]?.tid];
        }
      } else {
        return [];
      }
    };
    return (
      <thead ref={this.tableHeadRef}>
        <tr>
          {zAxis &&
            zAxis[0]?.axisOrdinates[0]?.ordinateItem?.map((item) => {
              const firstMemberHierarchyOrVariable =
                item?.items?.length > 0 ? item.items[0] : null;
              if (!firstMemberHierarchyOrVariable) {
                return null;
              }
              return (
                <React.Fragment key={item.object.tid}>
                  <th
                    colSpan={yAxes.length * 2}
                    style={{ border: "none" }}
                  ></th>
                  <th colSpan={3} className="axis-label">
                    <span className="cell-content">
                      {firstMemberHierarchyOrVariable.object.name}
                    </span>
                  </th>
                  {generateFillers(3, refXAxis.axisOrdinates.length * 2)}
                </React.Fragment>
              );
            })}
        </tr>
        <tr>
          {zAxis &&
            zAxis[0]?.axisOrdinates[0]?.ordinateItem?.map((item, index) => {
              const optionsForZ = getOptionsForZ(zAxis[0].axisOrdinates, item);
              return (
                <React.Fragment key={item.object.tid}>
                  <th
                    colSpan={yAxes.length * 2 + index}
                    style={{ border: "none" }}
                  ></th>
                  <th colSpan={3} className={`code`} style={{ zIndex: 1000 }}>
                    <span className="cell-content">
                      <Select
                        isMulti={
                          this.props.cubeQueryWizardStore.hasMembersInZAxis
                        }
                        isClearable={true}
                        options={optionsForZ}
                        placeholder={
                          this.props.cubeQueryWizardStore.selectAllForZAxis &&
                          this.props.cubeQueryWizardStore
                            .isTemplateRepresentationSelectionOn
                            ? "All selected"
                            : item.isOpenAxis
                            ? "Open Axis"
                            : "Member"
                        }
                        onChange={(values) => {
                          const selectedValues = Array.isArray(values)
                            ? values
                            : values
                            ? [values]
                            : [];

                          const tids = selectedValues.map((item) => item.value);
                          this.handleChange(
                            availableMember.filter((member) =>
                              tids.includes(member.tid)
                            ),
                            item.axisOrdinates ? item : zAxis[0],
                            index
                          );
                        }}
                        value={
                          this.props.cubeQueryWizardStore.selectAllForZAxis &&
                          this.props.cubeQueryWizardStore
                            .isTemplateRepresentationSelectionOn
                            ? []
                            : optionsForZ?.filter((option) =>
                                getValuesForZ(index).includes(option.value)
                              )
                        }
                        isDisabled={
                          (this.props.cubeQueryWizardStore.selectAllForZAxis &&
                            this.props.cubeQueryWizardStore
                              .isTemplateRepresentationSelectionOn) ||
                          item.isOpenAxis
                        }
                      />
                    </span>
                    {this.props.cubeQueryWizardStore.hasMembersInZAxis &&
                      this.props.cubeQueryWizardStore
                        .isTemplateRepresentationSelectionOn && (
                        <span>
                          <label
                            htmlFor="select-all-members"
                            className="select-all-members"
                          >
                            <input
                              id="select-all-members"
                              type="checkbox"
                              checked={
                                this.props.cubeQueryWizardStore
                                  .selectAllForZAxis
                              }
                              onChange={
                                this.props.cubeQueryWizardStore
                                  .setSelectAllForZAxis
                              }
                            />{" "}
                            Select all in Z axis
                          </label>
                        </span>
                      )}
                  </th>
                  {generateFillers(3, refXAxis.axisOrdinates.length)}
                </React.Fragment>
              );
            })}
        </tr>
        {xAxes.map((axis, index) => (
          <React.Fragment key={axis.axisId}>
            <tr>
              <th colSpan={yAxes.length * 2} style={{ border: "none" }}></th>
              <th colSpan={axis.axisOrdinates.length} className="axis-label">
                <span className="cell-content">{axis.orientation} axis</span>
              </th>
              {generateFillers(
                axis.axisOrdinates.length,
                refXAxis.axisOrdinates.length
              )}
            </tr>
            {[...Array(axis.maxLevelNo + 1).keys()].map((level) => (
              <tr className="x-axis-level" key={level}>
                <th
                  className={`${level === axis.maxLevelNo ? "select-all" : ""}`}
                  colSpan={yAxes.length * 2}
                  style={{ border: "none" }}
                >
                  {level === 0 &&
                    this.props.cubeQueryWizardStore.loadingCombinations && (
                      <React.Fragment>
                        <div className="query-wizard-loader"></div> Loading
                        combinations...
                      </React.Fragment>
                    )}
                  {this.props.cubeQueryWizardStore.canSelectMultipleCells() &&
                    level === axis.maxLevelNo && (
                      <button
                        className="btn btn-default select-all"
                        onClick={() =>
                          this.props.cubeQueryWizardStore.toggleSelectAllCells(
                            xAxes,
                            yAxes,
                            getSelectedTemplateMembers[0],
                            null,
                            itemIndex
                          )
                        }
                      >
                        {this.props.cubeQueryWizardStore.allCellsStateSelected
                          ? "Unselect"
                          : "Select"}{" "}
                        all cells
                      </button>
                    )}
                </th>
                {axis.axisOrdinates
                  .filter((ordinate) => ordinate.levelNo <= level)
                  .map((ordinate) => (
                    <th
                      key={ordinate.axisOrdinateId}
                      colSpan={
                        ordinate.levelNo === level
                          ? getChildrenNo(ordinate, axis.axisOrdinates) + 1
                          : 1
                      }
                      className={`${
                        ordinate.levelNo > level
                          ? `level${level}`
                          : `level${ordinate.levelNo}`
                      } ${ordinate.isAbstractHeader ? "shaded" : ""}`}
                    >
                      {ordinate.levelNo === level && ordinate.name ? (
                        <span>
                          <CellContent
                            maxLevelNo={axis}
                            content={ordinate.name}
                            id={ordinate.axisOrdinateId}
                          />
                        </span>
                      ) : null}
                    </th>
                  ))}
                {generateFillers(
                  axis.axisOrdinates.length,
                  refXAxis.axisOrdinates.length
                )}
              </tr>
            ))}
            <tr className={`${index === xAxes.length - 1 ? "sticky" : ""}`}>
              {index === xAxes.length - 1 ? (
                yAxes.map((axis) => (
                  <th
                    colSpan="2"
                    className="axis-label"
                    key={axis.axisId}
                    style={{ borderBottom: 0 }}
                  >
                    <span className="cell-content">
                      {axis.orientation} axis
                    </span>
                  </th>
                ))
              ) : (
                <th colSpan={yAxes.length * 2}></th>
              )}
              {axis.axisOrdinates.map((ordinate) => (
                <th
                  className={`code level${ordinate.levelNo} ${
                    ordinate.isAbstractHeader ? "shaded" : ""
                  }`}
                  key={ordinate.code}
                  style={{ borderBottom: 0 }}
                >
                  <span className="cell-content">
                    {this.props.cubeQueryWizardStore.canSelectMultipleCells() &&
                    !ordinate.isAbstractHeader ? (
                      <React.Fragment>
                        <label htmlFor={`column_${ordinate.code}`}>
                          <input
                            id={`column_${ordinate.code}`}
                            type="checkbox"
                            checked={
                              this.props.cubeQueryWizardStore.getTemplateRepresentationColumnSelectionState(
                                ordinate.code,
                                itemIndex
                              ) !== undefined
                            }
                            onChange={() =>
                              this.props.cubeQueryWizardStore.toggleTemplateRepresentationSelectColumn(
                                ordinate,
                                getSelectedTemplateMembers[0],
                                null,
                                itemIndex
                              )
                            }
                          />
                          &nbsp;{ordinate.code}
                        </label>
                      </React.Fragment>
                    ) : !ordinate.isAbstractHeader ? (
                      ordinate.code
                    ) : (
                      ""
                    )}
                  </span>
                </th>
              ))}
              {generateFillers(
                axis.axisOrdinates.length,
                refXAxis.axisOrdinates.length
              )}
            </tr>
          </React.Fragment>
        ))}
      </thead>
    );
  };

  renderTableBody(
    xAxes,
    yAxes,
    zAxis,
    refXAxis,
    refYAxis,
    combinations,
    itemIndex
  ) {
    const rows = [];
    const { showCombinationDetailsModal, details } = this.state;
    const indentLevel = (ordinate, currentLevel) => {
      return (
        <div
          className={`y-axis-level ${
            /* ordinate.isAbstractHeader ? "shaded" :  */ `level${currentLevel}`
          } `}
        >
          {ordinate.levelNo === currentLevel ? (
            ordinate.name ? (
              <span>
                <CellContent
                  content={ordinate.name}
                  id={ordinate.axisOrdinateId}
                />
              </span>
            ) : null
          ) : (
            indentLevel(ordinate, currentLevel + 1)
          )}
        </div>
      );
    };

    for (let y = 0; y < refYAxis.axisOrdinates.length; y++) {
      const cells = [];
      // adding Y axis cells
      yAxes.forEach((axis) => {
        if (axis.axisOrdinates[y]) {
          cells.push(
            <th
              className={`name ${
                axis.axisOrdinates[y].isAbstractHeader ? "shaded" : ""
              }`}
              key={`name-${axis.axisOrdinates[y].tid}`}
            >
              {indentLevel(axis.axisOrdinates[y], 0)}
              {this.props.cubeQueryWizardStore
                .isTemplateRepresentationSelectionOn &&
                // @TODO: Remove this line (ask @pavel.struhar)
                // !axis.axisOrdinates[y].isAbstractHeader &&
                this.props.cubeQueryWizardStore.isOrdinateLinkedToVariable(
                  axis.axisOrdinates[y]
                ) && (
                  <input
                    type="text"
                    placeholder="Type a value..."
                    value={
                      this.props.cubeQueryWizardStore.valueByVariableTid[
                        axis.axisOrdinates[y].variable.object.tid
                      ] || ""
                    }
                    onChange={(e) =>
                      this.props.cubeQueryWizardStore.setValueForOrdinateLinkedToVariable(
                        axis.axisOrdinates[y],
                        e.target.value
                      )
                    }
                  />
                )}
            </th>
          );
          cells.push(
            <th
              className={`code level${axis.axisOrdinates[y].levelNo} ${
                axis.axisOrdinates[y].isAbstractHeader ? "shaded" : ""
              }`}
              key={`code-${axis.axisOrdinates[y].tid}`}
            >
              <span className="cell-content">
                {this.props.cubeQueryWizardStore.canSelectMultipleCells() &&
                !axis.axisOrdinates[y].isAbstractHeader ? (
                  <React.Fragment>
                    <label htmlFor={`row_${axis.axisOrdinates[y].code}`}>
                      <input
                        id={`row_${axis.axisOrdinates[y].code}`}
                        type="checkbox"
                        checked={
                          this.props.cubeQueryWizardStore.getTemplateRepresentationRowSelectionState(
                            axis.axisOrdinates[y].code,
                            itemIndex
                          ) !== undefined
                        }
                        onChange={() =>
                          this.props.cubeQueryWizardStore.toggleTemplateRepresentationSelectRow(
                            axis.axisOrdinates[y],
                            this.context.getSelectedTemplateMembers[0],
                            null,
                            itemIndex
                          )
                        }
                      />
                      &nbsp;{axis.axisOrdinates[y].code}
                    </label>
                  </React.Fragment>
                ) : !axis.axisOrdinates[y].isAbstractHeader ? (
                  axis.axisOrdinates[y].code
                ) : (
                  ""
                )}
              </span>
            </th>
          );
        }
      });
      for (let x = 0; x < refXAxis.axisOrdinates.length; x++) {
        let targetCombinationId;

        const commonXY =
          !refYAxis.axisOrdinates[y].isAbstractHeader &&
          !refXAxis.axisOrdinates[x].isAbstractHeader
            ? refYAxis.axisOrdinates[y].combinationIds.filter(
                (value) =>
                  value &&
                  refXAxis.axisOrdinates[x].combinationIds.includes(value)
              )
            : [];
        const codeForX = refXAxis.axisOrdinates[x].code;
        const codeForY = refYAxis.axisOrdinates[y].code;
        if (
          zAxis &&
          this.context.getSelectedTemplateMembers &&
          Array.isArray(this.context.getSelectedTemplateMembers) &&
          this.context.getSelectedTemplateMembers.length >
            this.props.itemIndex &&
          this.context.getSelectedTemplateMembers[this.props.itemIndex] &&
          Array.isArray(
            this.context.getSelectedTemplateMembers[this.props.itemIndex]
          ) &&
          this.context.getSelectedTemplateMembers[this.props.itemIndex].length >
            0 &&
          this.context.getSelectedTemplateMembers[this.props.itemIndex][0] &&
          Array.isArray(
            this.context.getSelectedTemplateMembers[this.props.itemIndex][0]
          ) &&
          this.context.getSelectedTemplateMembers[this.props.itemIndex][0]
            .length > 0 &&
          this.context.getSelectedTemplateMembers[this.props.itemIndex][0][0]
            .combinationIds
        ) {
          const commonXYZ = commonXY.filter((combId) =>
            this.context.getSelectedTemplateMembers[
              this.props.itemIndex
            ][0][0].combinationIds.includes(combId)
          );
          if (commonXYZ.length > 0) {
            targetCombinationId = commonXYZ[0];
          }
        } else {
          if (commonXY.length > 0) {
            targetCombinationId = commonXY[0];
          }
        }
        const targetCombination = combinations.find(
          (comb) =>
            comb.combinationId && comb.combinationId === targetCombinationId
        );
        cells.push(
          <td
            key={`cell-${x}`}
            className={`${
              !targetCombination ||
              refYAxis.axisOrdinates[y].isAbstractHeader ||
              refXAxis.axisOrdinates[x].isAbstractHeader
                ? "shaded"
                : ""
            }`}
          >
            {targetCombination ? (
              <span className="cell-content combination-id">
                {this.props.cubeQueryWizardStore
                  .isTemplateRepresentationSelectionOn ? (
                  <React.Fragment>
                    <label
                      htmlFor={`combination_${targetCombination.combinationId}`}
                      className={`${
                        !this.props.cubeQueryWizardStore.isCombinationIdAllowed(
                          targetCombination.combinationId
                        )
                          ? "disabled"
                          : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        id={`combination_${targetCombination.combinationId}`}
                        checked={this.getCellState(
                          targetCombination.combinationId,
                          codeForX,
                          codeForY,
                          itemIndex
                        )}
                        disabled={
                          !this.props.cubeQueryWizardStore.isCombinationIdAllowed(
                            targetCombination.combinationId
                          )
                        }
                        onChange={() => {
                          this.props.cubeQueryWizardStore.toggleTemplateRepresentationCellState(
                            targetCombination.combinationId,
                            codeForX,
                            codeForY,
                            this.context.getSelectedTemplateMembers[
                              this.props.itemIndex
                            ],
                            null,
                            itemIndex
                          );
                        }}
                      />
                      {targetCombination.combinationId}
                    </label>
                  </React.Fragment>
                ) : (
                  <Link
                    onClick={(e) => {
                      e.preventDefault();
                      this.handleToggleCombinationDetailsModal(
                        targetCombination.type,
                        targetCombination.tid
                      );
                      // setType();
                      // setTid();
                    }}
                    to={`/view/${targetCombination.type}/${targetCombination.tid}`}
                    // target="_blank"
                  >
                    {targetCombination.combinationId}
                  </Link>
                )}
              </span>
            ) : null}
          </td>
        );
      }
      rows.push(<tr key={`row-${y}`}>{cells}</tr>);
    }
    return (
      <tbody ref={this.tableBodyRef}>
        {rows.length > 0 ? (
          <>
            {rows}
            {showCombinationDetailsModal && (
              <StoreContext.Provider value={this.props.popupStore}>
                <CombinationDetailsModal
                  variable={null}
                  cubeVariableName={"[No name]"}
                  cubeVariableCode={null}
                  details={details}
                  onClose={this.handleToggleCombinationDetailsModal}
                />
              </StoreContext.Provider>
            )}
          </>
        ) : (
          <tr>
            <td colSpan={yAxes.length + refXAxis.axisOrdinates.length}>
              <span className="cell-content">No records found.</span>
            </td>
          </tr>
        )}
      </tbody>
    );
  }

  makeHeadSticky = () => {
    if (!this.tableHeadRef || !this.tableHeadRef.current) return;
    let h1 = 0;
    Array.from(this.tableHeadRef.current.querySelectorAll("tr")).forEach(
      (tr) => {
        Array.from(tr.querySelectorAll("th")).forEach((th) => {
          th.style.top = `${h1}px`;
          th.style.position = "sticky";
        });
        h1 += tr.getBoundingClientRect().height;
      }
    );
  };
  getUniqueMembersCode = (members) => {
    const uniqueMember = [];
    members.forEach((member) => {
      const name = member.name;
      if (!uniqueMember.some((item) => item.name === name)) {
        uniqueMember.push(member);
      }
    });
    return uniqueMember;
  };

  getMembers = () => {
    const { getEntityData } = this.context;
    const entityData = getEntityData.item;
    let members =
      entityData &&
      entityData.otherInformation &&
      entityData.otherInformation.dpmTemplateZAxis &&
      entityData.otherInformation.dpmTemplateZAxis.length
        ? entityData.otherInformation.dpmTemplateZAxis
        : [];
    members = this.getUniqueMembersCode(members);
    return members;
  };

  getCombinations = () => {
    const { getEntityData } = this.context;
    const entityData = getEntityData.item;
    const combinations =
      entityData &&
      entityData.otherInformation &&
      entityData.otherInformation.dpmTemplateCombinations &&
      entityData.otherInformation.dpmTemplateCombinations.length
        ? entityData.otherInformation.dpmTemplateCombinations
        : [];
    return combinations;
  };

  getTableWidth = () => {
    if (!this.tableRef || !this.tableRef.current) return;
  };
  static contextType = StoreContext;
  render() {
    const { getEntityData } = this.context;
    const entityData = getEntityData.item;
    const { getSelectedTemplateMembers } = this.context;
    if (!getSelectedTemplateMembers) return null;

    const tableData = this.processEntityData(entityData);
    if (!tableData || !tableData.axes) return null;

    const zAxis = tableData.axes.filter((axis) => axis.orientation === "Z");
    const xAxes = tableData.axes
      .filter((axis) => axis.orientation === "X")
      .sort((a, b) => {
        if (a.orientation > b.orientation) return -1;
        if (a.orientation < b.orientation) return 1;
        return 0;
      });
    const yAxes = tableData.axes.filter((axis) => axis.orientation === "Y");

    const members = this.getMembers();

    const combinations = this.getCombinations();

    if (!xAxes || !yAxes) return null;

    const refXAxis = xAxes.find((axis) => {
      return (
        Math.max(...xAxes.map(() => axis.axisOrdinates.length)) ===
        axis.axisOrdinates.length
      );
    });

    const refYAxis = yAxes.find((axis) => {
      return (
        Math.max(...yAxes.map((a) => axis.axisOrdinates.length)) ===
        axis.axisOrdinates.length
      );
    });

    if (!refXAxis || !refYAxis) return null;

    return (
      <table className="template-table" ref={this.tableRef}>
        {this.renderTableHead(
          xAxes,
          yAxes,
          zAxis,
          refXAxis,
          refYAxis,
          members,
          this.props.itemIndex
        )}
        {this.renderTableBody(
          xAxes,
          yAxes,
          zAxis,
          refXAxis,
          refYAxis,
          combinations,
          this.props.itemIndex
        )}
      </table>
    );
  }
}

export default PivotTable;
